import VueRouter from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from '../store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import AnalyticsHelper from '../helper/AnalyticsHelper';

const routes = [...dashboard.routes];

window.roleWiseRoutes = {
  agent: [],
  administrator: [],
};

// generateRoleWiseRoute - updates window object with agent/admin route
const generateRoleWiseRoute = route => {
  route.forEach(element => {
    if (element.children) {
      generateRoleWiseRoute(element.children);
    }
    if (element.roles) {
      element.roles.forEach(roleEl => {
        window.roleWiseRoutes[roleEl].push(element.name);
      });
    }
  });
};
// Create a object of routes
// accessible by each role.
// returns an object with roles as keys and routeArr as values
generateRoleWiseRoute(routes);

export const router = new VueRouter({ mode: 'history', routes });

export const validateAuthenticateRoutePermission = (to, next, { getters }) => {
  const { isLoggedIn, getCurrentUser: user } = getters;

  if (!isLoggedIn) {
    window.location = '/app/login';
    return '/app/login';
  }

  if (!to.name) {
    return next(frontendURL(`accounts/${user.account_id}/dashboard`));
  }

  const nextRoute = validateLoggedInRoutes(
    to,
    getters.getCurrentUser,
    window.roleWiseRoutes
  );
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

// A tab that was open across a deploy is holding an index that names chunks the server has
// already deleted. Every settings, report and inbox page is a lazy `import()`, so the next
// navigation asks for a file that is gone, the promise rejects, `next()` is never called, and the
// route simply never resolves — the app sits on its loading state with nothing in the console but
// an unhandled rejection.
//
// It is not a caching bug: fingerprinted assets are served with a year of cache and the HTML
// revalidates on every load, both correct. The gap is that a RUNNING tab has no way to learn that
// the build under it has been replaced.
//
// So the tab reloads itself. It comes back on the current index, fetches the current chunks, and
// lands on the route the user actually asked for.
const CHUNK_FAILURE =
  /Loading (CSS )?chunk .* failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;
const RELOAD_MARKER = 'chunk-reload-at';
// Long enough that a genuinely missing chunk cannot become a reload loop, short enough that a
// second deploy an hour later still recovers on its own.
const RELOAD_COOLDOWN_MS = 10_000;

const recoverFromStaleChunk = error => {
  if (!CHUNK_FAILURE.test(error?.message || '')) return false;

  // One reload, then stop. If the chunk is missing for any reason OTHER than a deploy — a broken
  // build, a blocked request — reloading again would spin the tab forever, which is a worse
  // failure than the one being fixed.
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_MARKER)) || 0;
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
  } catch (storageError) {
    // Private browsing, or site data blocked. Reload anyway — a single reload without the guard
    // is still better than a permanent spinner.
  }

  window.location.reload();
  return true;
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  router.onError(error => {
    recoverFromStaleChunk(error);
  });

  router.beforeEach((to, from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    userAuthentication
      .then(() => {
        return validateAuthenticateRoutePermission(to, next, store);
      })
      // Without this the guard is a second silent hang: `setUser` or the permission check
      // throwing leaves `next()` uncalled and the route unresolved, with no error surfaced.
      // Letting the navigation proceed is the safe direction — the route's own auth still
      // applies, and a visible page beats a spinner that never ends.
      .catch(error => {
        if (recoverFromStaleChunk(error)) return;

        // eslint-disable-next-line no-console
        console.error('Route guard failed, continuing to the route:', error);
        next();
      });
  });
};

export default router;
